import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { CalendarDays, Loader2 } from "lucide-react";

const MESES_NOME = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

const BR = (opts) => new Intl.DateTimeFormat("en-CA", { timeZone: "America/Sao_Paulo", ...opts });
const anoBR = () => BR({ year: "numeric" }).format(new Date());
const mesBR = () => String(Number(BR({ month: "numeric" }).format(new Date())));

export const MovimentacaoDiaria = ({ tipoRegistro, diaSelecionado, onSelecionarDia, refreshKey = 0 }) => {
  const hoje = new Date();
  const [ano, setAno] = useState(anoBR());
  const [mes, setMes] = useState(mesBR());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get("/stats/diario", {
        params: { ano: Number(ano), mes: Number(mes), tipo_registro: tipoRegistro },
      });
      setData(res);
    } finally {
      setLoading(false);
    }
  }, [ano, mes, tipoRegistro]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const max = Math.max(1, ...(data?.dias || []).map((d) => d.movimentados));

  return (
    <div className="border border-border bg-white mb-6" data-testid="movimentacao-diaria">
      <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-border">
        <div className="flex items-center gap-2">
          <CalendarDays className="h-4 w-4 text-slate-400" />
          <p className="text-[11px] uppercase tracking-wider text-slate-500">
            Processos movimentados por dia
          </p>
        </div>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
        <div className="flex items-center gap-2 ml-auto">
          <select
            value={mes}
            onChange={(e) => setMes(e.target.value)}
            data-testid="diario-mes-filter"
            className="h-8 border border-input bg-white px-2 text-xs rounded-md"
          >
            {MESES_NOME.map((m, i) => (
              <option key={m} value={i + 1}>{m}</option>
            ))}
          </select>
          <select
            value={ano}
            onChange={(e) => setAno(e.target.value)}
            data-testid="diario-ano-filter"
            className="h-8 border border-input bg-white px-2 text-xs rounded-md mono"
          >
            {(data?.anos || [hoje.getFullYear()]).map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </div>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-px bg-border">
            {[
              ["total", "Movimentados", data.total_movimentados],
              ["entradas", "Entradas", data.total_entradas],
              ["envios", "Enviados", data.total_envios],
              ["conclusoes", "Concluídos", data.total_conclusoes],
              ["trocas", "Trocas de setor", data.total_trocas_setor ?? 0],
              ["media", "Média/dia útil", data.media_por_dia],
              ["dias", "Dias com movimento", data.dias_com_movimento],
            ].map(([k, l, v]) => (
              <div key={l} className="bg-white px-4 py-3" data-testid={`diario-kpi-${k}`}>
                <p className="text-[10px] uppercase tracking-wider text-slate-500">{l}</p>
                <p className="text-lg font-semibold font-display mono mt-0.5">{v}</p>
              </div>
            ))}
          </div>

          <div className="p-4 overflow-x-auto table-scroll">
            <div className="flex items-end gap-1 min-w-[680px] h-32">
              {data.dias.map((d) => {
                const ativo = diaSelecionado === d.data;
                const fds = d.semana === "SÁB" || d.semana === "DOM";
                return (
                  <button
                    key={d.data}
                    onClick={() => onSelecionarDia(ativo ? null : d.data)}
                    data-testid={`diario-bar-${d.data}`}
                    title={`${d.dia}/${mes} (${d.semana}) — ${d.movimentados} movimentação(ões): ${d.entradas} entrada(s), ${d.envios} envio(s), ${d.conclusoes} conclusão(ões), ${d.trocas_setor ?? 0} troca(s) de setor. Clique para ver os processos`}
                    className="flex-1 flex flex-col items-center justify-end h-full group"
                  >
                    <span className={`text-[9px] mono mb-1 transition-opacity duration-150 ${
                      ativo ? "text-primary opacity-100" : "text-slate-500 opacity-0 group-hover:opacity-100"
                    }`}>
                      {d.movimentados || ""}
                    </span>
                    <div
                      className={`w-full transition-all duration-300 ${
                        ativo
                          ? "bg-primary ring-2 ring-primary/40"
                          : d.movimentados === 0
                          ? "bg-slate-100"
                          : fds
                          ? "bg-amber-400/80 group-hover:bg-amber-500"
                          : "bg-primary/80 group-hover:bg-primary"
                      }`}
                      style={{ height: `${Math.max(Math.round((d.movimentados / max) * 100), d.movimentados ? 4 : 2)}%` }}
                    />
                    <span className={`text-[9px] mono mt-1 ${ativo ? "text-primary font-medium" : fds ? "text-amber-600" : "text-slate-400"}`}>
                      {d.dia}
                    </span>
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] text-slate-400 mt-2">
              Movimentação = entrada, envio, conclusão ou qualquer salvamento do processo no dia.
              Clique em um dia para ver os processos (barras em amarelo são fins de semana)
            </p>
          </div>
        </>
      )}
    </div>
  );
};
